import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2,
  Loader2,
  Clock,
  XCircle,
  Send,
  Home,
  Receipt,
  Mail,
  ArrowRight,
} from "lucide-react";
import { SiteHeader } from "../components/site/SiteHeader";
import { MobileBottomBar } from "../components/site/MobileBottomBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const AGENT_TELEGRAM =
  process.env.REACT_APP_AGENT_TELEGRAM || "https://t.me/Maxlive88_Agents_Support";

/**
 * Landing page users hit after returning from the payment gateway.
 * We poll the application status every 3s (max ~2 min) so a webhook that
 * arrives moments later still flips the UI to "PAID" without user refresh.
 */
export default function AgentApplyReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const appId = params.get("app") || "";
  const [state, setState] = useState({ loading: true, doc: null, error: "" });
  const [ticks, setTicks] = useState(0);

  useEffect(() => {
    if (!appId) {
      setState({ loading: false, doc: null, error: "Application id missing" });
      return;
    }
    let stopped = false;
    let iv;

    const fetchStatus = async () => {
      try {
        const { data } = await axios.get(`${BACKEND}/api/site/agent-applications/${appId}`);
        if (stopped) return;
        setState({ loading: false, doc: data, error: "" });
        // Stop polling once terminal state reached
        if (["paid", "failed"].includes((data.payment_status || "").toLowerCase()) ||
            ["approved", "rejected"].includes((data.status || "").toLowerCase())) {
          if (iv) clearInterval(iv);
        }
      } catch (err) {
        if (stopped) return;
        setState({ loading: false, doc: null, error: err?.response?.data?.detail || "Not found" });
        if (iv) clearInterval(iv);
      }
    };

    fetchStatus();
    iv = setInterval(() => {
      setTicks((t) => t + 1);
      fetchStatus();
    }, 3000);
    // Auto-stop polling after 2 minutes
    const stopTo = setTimeout(() => { if (iv) clearInterval(iv); }, 120000);
    return () => { stopped = true; if (iv) clearInterval(iv); clearTimeout(stopTo); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appId]);

  const doc = state.doc;
  const paid = doc && (doc.payment_status === "paid");
  const failed = doc && (doc.payment_status === "failed");
  const status = doc?.status || "payment_pending";

  return (
    <div className="min-h-screen bg-[#080d13] text-white pb-24 lg:pb-20">
      <div className="hidden lg:block"><SiteHeader /></div>

      <div className="max-w-2xl mx-auto px-4 lg:px-6 pt-10 lg:pt-16">
        <div className="relative overflow-hidden rounded-3xl border border-white/5 bg-gradient-to-b from-[#0f1620] to-[#0a1119] p-6 lg:p-10">
          <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl" />

          {state.loading && (
            <PendingBlock title="Checking payment status..." subtitle="One moment please" />
          )}

          {!state.loading && state.error && (
            <ErrorBlock message={state.error} onHome={() => navigate("/")} />
          )}

          {!state.loading && doc && paid && (
            <SuccessBlock doc={doc} onHome={() => navigate("/")} />
          )}

          {!state.loading && doc && failed && (
            <FailedBlock doc={doc} onRetry={() => navigate("/become-agent")} />
          )}

          {!state.loading && doc && !paid && !failed && (
            <PendingBlock
              title="Waiting for payment confirmation"
              subtitle={`Payment status will update automatically. Attempt ${ticks + 1}...`}
              extra={<StatusPills doc={doc} />}
            />
          )}

          {/* Approved / rejected banner if applicable */}
          {!state.loading && doc && (status === "approved" || status === "rejected") && (
            <div className={`mt-6 rounded-2xl border p-4 flex items-start gap-3 ${
              status === "approved" ? "border-emerald-500/30 bg-emerald-500/10" : "border-amber-500/30 bg-amber-500/10"
            }`}>
              {status === "approved" ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
              ) : (
                <XCircle className="w-5 h-5 text-amber-400 mt-0.5" />
              )}
              <div className="text-sm">
                {status === "approved"
                  ? "Your agent application has been approved! Please check your email + Telegram for account setup instructions."
                  : "Your agent application was not approved. Please check your email for details, or contact Admin on Telegram."}
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-center">
          <a href={AGENT_TELEGRAM} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#229ED9] hover:bg-[#1E8ABF] text-white text-sm font-bold"
             data-testid="return-telegram">
            <Send className="w-4 h-4" /> Message Admin on Telegram
          </a>
        </div>
      </div>

      <div className="lg:hidden"><MobileBottomBar /></div>
    </div>
  );
}

function ShortRef({ id }) {
  const r = "AA-" + (id?.slice(0, 8).toUpperCase().replace(/-/g, "") || "").slice(0, 6);
  return <span className="font-mono">{r}</span>;
}

function PendingBlock({ title, subtitle, extra }) {
  return (
    <div className="relative text-center py-6" data-testid="return-pending">
      <div className="w-20 h-20 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center mx-auto">
        <Loader2 className="w-10 h-10 text-amber-400 animate-spin" />
      </div>
      <div className="mt-5 text-2xl font-black">{title}</div>
      <div className="text-sm text-white/60 mt-2 max-w-md mx-auto">{subtitle}</div>
      {extra}
    </div>
  );
}

function SuccessBlock({ doc, onHome }) {
  const amount = doc.paid_amount || 13000;
  return (
    <div className="relative text-center py-6" data-testid="return-success">
      <div className="w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto">
        <CheckCircle2 className="w-10 h-10 text-emerald-400" />
      </div>
      <div className="mt-5 text-2xl lg:text-3xl font-black">Deposit successful!</div>
      <div className="text-sm text-white/60 mt-2 max-w-md mx-auto leading-relaxed">
        আপনার ৳{Number(amount).toLocaleString("en-IN")} সরাসরি Agent Balance এ credit হয়েছে। PDF receipt সহ email
        পাঠানো হয়েছে <span className="text-emerald-300 font-semibold">{doc.email}</span> এ।
      </div>

      <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.06] p-5 text-left max-w-md mx-auto space-y-2">
        <Row label="Receipt #" value={<ShortRef id={doc.id} />} />
        <Row label="Deposited" value={`BDT ${Number(amount).toLocaleString("en-IN")}`} accent />
        <Row label="Status" value={<span className="text-emerald-300 font-bold">CREDITED</span>} />
        <Row label="Application" value={<span className="uppercase text-white/70">{doc.status || "pending"}</span>} />
      </div>

      <div className="mt-6 rounded-xl border border-white/5 bg-[#0a1119] p-4 max-w-md mx-auto flex items-start gap-3 text-left">
        <Mail className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
        <div className="text-xs text-white/70 leading-relaxed">
          Check your inbox (and spam folder). Our admin team will review your documents and
          send an approval/rejection email within 24 hours.
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3 mt-7">
        <button onClick={onHome}
                className="px-5 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold inline-flex items-center gap-2"
                data-testid="return-home">
          <Home className="w-4 h-4" /> Back to home
        </button>
      </div>
    </div>
  );
}

function FailedBlock({ doc, onRetry }) {
  return (
    <div className="relative text-center py-6" data-testid="return-failed">
      <div className="w-20 h-20 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mx-auto">
        <XCircle className="w-10 h-10 text-red-400" />
      </div>
      <div className="mt-5 text-2xl font-black">Payment failed</div>
      <div className="text-sm text-white/60 mt-2 max-w-md mx-auto">
        আপনার payment complete হয়নি। আবার চেষ্টা করুন — অথবা অন্য gateway দিয়ে try করুন।
      </div>
      <div className="mt-4 text-xs text-white/40">Application <ShortRef id={doc.id} /></div>
      <button onClick={onRetry}
              className="mt-6 px-6 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-bold inline-flex items-center gap-2"
              data-testid="return-retry">
        Try again <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function ErrorBlock({ message, onHome }) {
  return (
    <div className="text-center py-6">
      <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto">
        <XCircle className="w-8 h-8 text-white/60" />
      </div>
      <div className="mt-5 text-xl font-bold">Application not found</div>
      <div className="text-sm text-white/60 mt-1">{message}</div>
      <button onClick={onHome}
              className="mt-6 px-5 py-2 rounded-full bg-white/5 hover:bg-white/10 text-sm border border-white/10 inline-flex items-center gap-2">
        <Home className="w-4 h-4" /> Home
      </button>
    </div>
  );
}

function StatusPills({ doc }) {
  const ps = (doc.payment_status || "pending").toLowerCase();
  return (
    <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-200">
      <Clock className="w-3.5 h-3.5" />
      Payment status: <span className="font-bold uppercase">{ps}</span>
    </div>
  );
}

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-white/50">{label}</span>
      <span className={`${accent ? "text-emerald-400 font-black text-lg" : "text-white"} font-medium`}>{value}</span>
    </div>
  );
}
